"use client";

import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { DESKTOP_QUERY } from "@/lib/breakpoints";
type SnapshotAnimator = (name: string, entering: boolean) => (() => void) | undefined;
export function useHistoryTransition(pathname: string, contentRef: RefObject<HTMLDivElement | null>, animateSnapshot: SnapshotAnimator, enabled: boolean = true) {
  const committedPath = useRef(pathname);
  const pendingCommit = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    if (!enabled) return;
    committedPath.current = pathname;
    pendingCommit.current?.();
  }, [pathname, enabled]);
  useEffect(() => {
    if (!enabled) return;
    let replaying = false;
    let sequence = 0;
    let cancelActive: (() => void) | undefined;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onPopState = (event: PopStateEvent) => {
      // Desktop only: document.startViewTransition support and behavior is
      // inconsistent across mobile browsers/in-app webviews, and this custom
      // replay-and-animate flow relies on it resolving cleanly. On a phone
      // that just means falling back to Next's default (instant) back/forward
      // navigation instead of risking a stuck transition.
      const isDesktopWidth = window.matchMedia(DESKTOP_QUERY).matches;
      if (!isDesktopWidth || replaying || motion.matches || !document.startViewTransition || location.pathname === committedPath.current || !contentRef.current) return;

      // Next's cached history restore commits synchronously in this version.
      // Capture first, then deliver the SAME history state to Next and existing
      // listeners. No push/replace, router patches, or synthetic history entries.
      event.stopImmediatePropagation();
      cancelActive?.();
      const id = ++sequence;
      const oldName = `dezine-history-old-${id}`;
      const newName = `dezine-history-new-${id}`;
      const cleanups: Array<() => void> = [];
      let replayed = false;
      let transition: ViewTransition | undefined;
      let resolveCommit: (() => void) | undefined;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let finished = false;
      const tag = (element: HTMLElement, name: string) => {
        const previousName = element.style.getPropertyValue("view-transition-name");
        const previousClass = element.style.getPropertyValue("view-transition-class");
        element.style.setProperty("view-transition-name", name);
        element.style.setProperty("view-transition-class", "dezine-route");
        cleanups.push(() => {
          if (element.style.getPropertyValue("view-transition-name") !== name) return;
          if (previousName) element.style.setProperty("view-transition-name", previousName);else element.style.removeProperty("view-transition-name");
          if (previousClass) element.style.setProperty("view-transition-class", previousClass);else element.style.removeProperty("view-transition-class");
        });
      };
      const replay = () => {
        if (replayed) return;
        replayed = true;
        replaying = true;
        try {
          window.dispatchEvent(new PopStateEvent("popstate", {
            state: event.state
          }));
        } finally {
          replaying = false;
        }
      };
      const cleanup = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        resolveCommit?.();
        if (pendingCommit.current === resolveCommit) pendingCommit.current = null;
        cleanups.splice(0).reverse().forEach(clean => clean());
      };
      cancelActive = () => {
        transition?.skipTransition();
        cleanup();
        replay();
      };
      tag(contentRef.current, oldName);
      try {
        transition = document.startViewTransition(async () => {
          if (finished) {
            replay();
            return;
          }
          await new Promise<void>(resolve => {
            resolveCommit = () => {
              if (!finished && contentRef.current) tag(contentRef.current, newName);
              if (pendingCommit.current === resolveCommit) pendingCommit.current = null;
              resolve();
            };
            pendingCommit.current = resolveCommit;
            // A failed/aborted navigation must never leave rendering suspended.
            timer = setTimeout(() => cancelActive?.(), 2000);
            replay();
          });
        });
        void transition.ready.then(() => {
          if (finished) return;
          clearTimeout(timer);
          // Let React finish its scheduled root-animation cleanup first.
          // Otherwise a cached restore can cancel the header's new animation.
          let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => {
              if (finished) return;
              const oldCleanup = animateSnapshot(oldName, false);
              const newCleanup = animateSnapshot(newName, true);
              if (oldCleanup) cleanups.push(oldCleanup);
              if (newCleanup) cleanups.push(newCleanup);
            });
          });
          cleanups.push(() => cancelAnimationFrame(frame));
        }).catch(() => {});
        void transition.finished.then(cleanup, cleanup);
      } catch {
        cleanup();
        replay();
      }
    };
    const cancelForReducedMotion = () => {
      if (motion.matches) cancelActive?.();
    };
    window.addEventListener("popstate", onPopState, true);
    motion.addEventListener("change", cancelForReducedMotion);
    return () => {
      window.removeEventListener("popstate", onPopState, true);
      motion.removeEventListener("change", cancelForReducedMotion);
      cancelActive?.();
    };
  }, [contentRef, animateSnapshot, enabled]);
}